import { useEffect, useMemo, useRef, useState } from 'react'
import {
  legacyMonthlyContractSummary,
  legacyMonthlyFilterGroups,
  legacyMonthlyHolidays,
  legacyMonthlyMockEntries,
  legacyMonthlyOffices,
  type MonthlyFilterKey,
  type MonthlyMockEntry,
} from '../../mock/legacyMonthlySchedule'
import { defaultMonthlyFilters, LegacyMonthlyFilter, type MonthlyFilters } from './LegacyMonthlyFilter'
import './LegacyMonthlySchedule.css'

const backYearIcon = '/legacy/common/img/chevron-double-right.png'
const backMonthIcon = '/legacy/common/img/svg/icon_left_arrow_bold_black.svg'
const frontMonthIcon = '/legacy/common/img/svg/icon_right_arrow_bold_black.svg'
const frontYearIcon = '/legacy/common/img/chevron-double-left.png'

type CalendarCell = { day: number; monthOffset: -1 | 0 | 1; row: number; column: number }
type MonthlyRoute = { office: string; year: number; month: number }

const dataQueryKeys: Array<[string, string]> = [
  ['photo', 'photo_yn'], ['normal', 'normal_yn'], ['work', 'work_yn'], ['tm', 'tm_yn'],
  ['due', 'baby_duedate_yn'], ['birthday', 'baby_birth_yn'], ['party', 'baby_birthparty_day_yn'],
  ['memo', 'memo_yn'], ['trip', 'business_trip_yn'],
]

function cloneFilters(filters: MonthlyFilters): MonthlyFilters {
  return Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, [...values]])) as MonthlyFilters
}

function monthValue(year: number, month: number) { return `${year}-${String(month).padStart(2, '0')}` }

function parseRoute(): MonthlyRoute {
  const parts = window.location.pathname.split('/').filter(Boolean)
  const office = parts[3] || 'mock-office'
  const year = Number(parts[4]) || 2026
  const month = Number(parts[5]) || 8
  return { office, year, month }
}

function parseFilters(search = window.location.search): MonthlyFilters {
  const params = new URLSearchParams(search)
  if (params.size === 0 || (!params.has('shoot_status') && params.get('is_first') === 'Y')) return cloneFilters(defaultMonthlyFilters)
  const split = (key: string) => (params.get(key) ?? '').split(',').filter(Boolean)
  const tmStatusRaw = split('tm_status')
  const tmStatus = tmStatusRaw.filter((value) => value !== '0' && value !== '1')
  if (tmStatusRaw.includes('0') || tmStatusRaw.includes('1')) tmStatus.splice(tmStatus.includes('tm_status_all') ? 1 : 0, 0, '0,1')
  return {
    shootStatus: split('shoot_status'),
    contractType: split('contract_type'),
    tmCategory: split('tm_category'),
    tmStatus,
    data: dataQueryKeys.filter(([, queryKey]) => params.get(queryKey) === 'Y').map(([id]) => id),
  }
}

function filterQuery(filters: MonthlyFilters) {
  const tmStatus = filters.tmStatus.flatMap((value) => value === '0,1' ? ['0', '1'] : [value])
  const pairs: Array<[string, string]> = [
    ['shoot_status', filters.shootStatus.join(',')],
    ['tm_category', filters.tmCategory.join(',')],
    ['tm_status', tmStatus.join(',')],
    ['contract_type', filters.contractType.join(',')],
    ...dataQueryKeys.map(([id, queryKey]) => [queryKey, filters.data.includes(id) ? 'Y' : ''] as [string, string]),
  ]
  const first = new URLSearchParams(window.location.search).get('is_first')
  if (first) pairs.push(['is_first', first])
  return pairs.map(([key, value]) => `${key}=${value}`).join('&')
}

function createCalendarCells(year: number, month: number): CalendarCell[] {
  const firstDay = new Date(year, month - 1, 1).getDay()
  const currentDays = new Date(year, month, 0).getDate()
  const previousDays = new Date(year, month - 1, 0).getDate()
  const count = Math.ceil((firstDay + currentDays) / 7) * 7
  return Array.from({ length: count }, (_, index) => {
    const value = index - firstDay + 1
    const monthOffset: -1 | 0 | 1 = value < 1 ? -1 : value > currentDays ? 1 : 0
    const day = monthOffset === -1 ? previousDays + value : monthOffset === 1 ? value - currentDays : value
    return { day, monthOffset, row: Math.floor(index / 7), column: index % 7 }
  })
}

function lunarTitle(year: number, month: number, day: number) {
  const parts = new Intl.DateTimeFormat('ko-KR-u-ca-chinese', { year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date(year, month - 1, day))
  const relatedYear = parts.find((part) => (part.type as string) === 'relatedYear')?.value ?? String(year)
  const lunarMonth = (parts.find((part) => part.type === 'month')?.value ?? String(month)).replace('윤', '(윤)')
  const lunarDay = parts.find((part) => part.type === 'day')?.value ?? String(day)
  return `음력 ${relatedYear}년 ${lunarMonth}월 ${lunarDay}일`
}

function entryVisible(entry: MonthlyMockEntry, filters: MonthlyFilters) {
  const kindKey = entry.kind === 'birthday' ? 'birthday' : entry.kind
  if (!filters.data.includes(kindKey)) return false
  if (filters.data.includes('trip') && !entry.businessTrip) return false
  if (entry.kind === 'photo' && entry.shootStatus && !filters.shootStatus.includes(entry.shootStatus)) return false
  if (entry.kind === 'photo' && entry.contractType && !filters.contractType.includes(entry.contractType)) return false
  if (entry.kind === 'tm' && entry.tmCategory && !filters.tmCategory.includes(entry.tmCategory)) return false
  if (entry.kind === 'tm' && entry.tmStatus && !filters.tmStatus.includes(entry.tmStatus)) return false
  return true
}

function CalendarEntry({ entry, onOpen }: { entry: MonthlyMockEntry; onOpen: (entry: MonthlyMockEntry) => void }) {
  if (entry.kind === 'memo') return <div className="calendar_memo"><p>{entry.label}</p></div>
  if (entry.kind === 'due') return <div className="calendar_baby_duedate_box"><button className="calendar_baby_duedate" type="button" style={{ color: entry.color }} onClick={() => onOpen(entry)}>{entry.label}</button></div>
  if (entry.kind === 'tm') return <span className="calendar_tmschedulelist_box"><button className="tmschedulelist_list" type="button" style={{ color: entry.color }} onClick={() => onOpen(entry)}>{entry.label}</button></span>
  if (entry.kind === 'birthday' || entry.kind === 'party') return <div className="calendar_firstbirthpartylist_box"><button className="calendar_firstbirthpartylist_list" type="button" style={{ color: entry.color }} onClick={() => onOpen(entry)}><input className="sms_input" type="checkbox" aria-label="Mock 일정 문자 선택" onClick={(event) => event.stopPropagation()} />{entry.label}</button></div>
  const boxClass = entry.kind === 'photo' ? 'calendar_monthlyschedule_box' : 'calendar_current_monthlyschedule_box'
  return <div className={boxClass}><button className="monthlyschedule_list" type="button" style={{ color: entry.color }} title="Mock 일정 상세" onClick={() => onOpen(entry)}><input className="sms_input" type="checkbox" aria-label="Mock 일정 문자 선택" onClick={(event) => event.stopPropagation()} /><span className="list_schedule_title">{entry.time ? `${entry.time} ` : ''}{entry.label}</span><span className="list_office_name">Mock 지점</span></button></div>
}

export function LegacyMonthlySchedule() {
  const [route, setRoute] = useState<MonthlyRoute>(parseRoute)
  const [appliedFilters, setAppliedFilters] = useState<MonthlyFilters>(parseFilters)
  const [draftFilters, setDraftFilters] = useState<MonthlyFilters>(() => cloneFilters(parseFilters()))
  const [filterOpen, setFilterOpen] = useState(false)
  const [officeOpen, setOfficeOpen] = useState(false)
  const officeRef = useRef<HTMLDivElement>(null)
  const cells = useMemo(() => createCalendarCells(route.year, route.month), [route.year, route.month])
  const value = monthValue(route.year, route.month)

  const replaceLocation = (nextRoute: MonthlyRoute, nextFilters: MonthlyFilters, replace = false) => {
    const path = `/v1/schedule/monthly/${nextRoute.office}/${nextRoute.year}/${nextRoute.month}/`
    const url = `${path}?${filterQuery(nextFilters)}`
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url)
    setRoute(nextRoute)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.size === 0 || (!params.has('shoot_status') && params.get('is_first') === 'Y')) replaceLocation(route, appliedFilters, true)
    const sync = () => { setRoute(parseRoute()); const parsed = parseFilters(); setAppliedFilters(parsed); setDraftFilters(cloneFilters(parsed)) }
    window.addEventListener('popstate', sync)
    return () => window.removeEventListener('popstate', sync)
  // Initial URL normalization mirrors the original page load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!officeOpen) return
    const close = (event: MouseEvent) => { if (!officeRef.current?.contains(event.target as Node)) setOfficeOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [officeOpen])

  const changeMonth = (offset: number) => {
    const next = new Date(route.year, route.month - 1 + offset, 1)
    replaceLocation({ ...route, year: next.getFullYear(), month: next.getMonth() + 1 }, appliedFilters)
  }

  const changeMonthValue = (nextValue: string) => {
    const [year, month] = nextValue.split('-').map(Number)
    replaceLocation({ ...route, year, month }, appliedFilters)
  }

  const onToggle = (key: MonthlyFilterKey, id: string) => {
    setDraftFilters((current) => {
      const next = cloneFilters(current)
      if (id === 'all') {
        if (key === 'data') {
          const full = ['photo', 'normal', 'work', 'tm', 'due', 'birthday', 'party', 'memo']
          const turnOn = !full.every((item) => current.data.includes(item))
          next.data = turnOn ? [...full, ...(current.data.includes('trip') ? ['trip'] : [])] : current.data.filter((item) => item === 'trip')
          next.tmCategory = turnOn ? legacyMonthlyFilterGroups.find((group) => group.key === 'tmCategory')!.options.map((item) => item.id) : []
          next.tmStatus = turnOn ? legacyMonthlyFilterGroups.find((group) => group.key === 'tmStatus')!.options.map((item) => item.id) : []
          return next
        }
        const options = legacyMonthlyFilterGroups.find((group) => group.key === key)!.options.filter((item) => item.id !== 'all' && !item.disabled).map((item) => item.id)
        next[key] = options.every((item) => current[key].includes(item)) ? [] : options
        if ((key === 'tmCategory' || key === 'tmStatus') && next[key].length) next.data = [...new Set([...next.data, 'tm'])]
        return next
      }
      next[key] = current[key].includes(id) ? current[key].filter((item) => item !== id) : [...current[key], id]
      if (key === 'tmCategory' || key === 'tmStatus') {
        const hasTmChild = (key === 'tmCategory' ? next.tmCategory : next.tmStatus).length > 0
        next.data = hasTmChild ? [...new Set([...next.data, 'tm'])] : (next.tmCategory.length || next.tmStatus.length ? next.data : next.data.filter((item) => item !== 'tm'))
      }
      return next
    })
  }

  const applyFilters = () => {
    const next = cloneFilters(draftFilters)
    setAppliedFilters(next)
    replaceLocation(route, next, true)
    setFilterOpen(false)
  }

  const openFilter = () => { setDraftFilters(cloneFilters(appliedFilters)); setFilterOpen(true) }
  const closeFilter = () => { setDraftFilters(cloneFilters(appliedFilters)); setFilterOpen(false) }
  const currentQuery = () => window.location.search
  const openDetail = (day: number) => window.open(`/v1/schedule/monthly/${route.office}/${route.year}/${String(route.month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${currentQuery()}`, '_blank')
  const openMemo = (day: number) => window.open(`/write_schedulememo/${route.year}/${route.month}/${day}/${route.office}`, '_blank', 'width=800,height=630,scrollbars=yes')
  const openEntry = (entry: MonthlyMockEntry) => {
    if (entry.customerId) window.open(`/v1/customer/?CRUD=2&id=${encodeURIComponent(entry.customerId)}&caller=monthly_schedule`, '_blank')
    else window.open(`/v1/work_schedule?CRUD=3&detail_id=${encodeURIComponent(entry.id)}&caller=monthly_schedule`, '_blank', 'width=900,height=800,scrollbars=yes')
  }
  const totalContracts = legacyMonthlyContractSummary.reduce((sum, item) => sum + item.count, 0)
  const selectedOffice = legacyMonthlyOffices.find((item) => item.id === route.office) ?? legacyMonthlyOffices[0]
  const today = new Date()

  return <div id="l_monthly_sch_div" className="g_content_wrap g_content_full_size legacy-s02-page">
    <section className="result_box_wrap s02-result-box-wrap">
      <div className="g_row gap_10 margin_bottom_10 g_input_wrap s02-top-row">
        <div className="select_month_prev_next s02-select-month-prev-next" aria-label="월 이동">
          <button id="back_year_btn" className="g_btn size_1 s02-nav-btn s02-year-btn" type="button" aria-label="이전 연도" onClick={() => changeMonth(-12)}><img src={backYearIcon} alt="" /></button>
          <button id="back_btn" className="g_btn size_1 l_back_btn l_montly_arrow_btn s02-nav-btn s02-month-btn" type="button" aria-label="이전 달" onClick={() => changeMonth(-1)}><img src={backMonthIcon} alt="" /></button>
          <select id="l_sel_year_month" className="g_input" value={value} onChange={(event) => changeMonthValue(event.target.value)} aria-label="연월 선택">
            {Array.from({ length: 84 }, (_, index) => { const date = new Date(route.year - 3, index, 1); const next = monthValue(date.getFullYear(), date.getMonth() + 1); return <option key={next} value={next}>{next}</option> })}
          </select>
          <button id="front_btn" className="g_btn size_1 l_front_btn l_montly_arrow_btn s02-nav-btn s02-month-btn" type="button" aria-label="다음 달" onClick={() => changeMonth(1)}><img src={frontMonthIcon} alt="" /></button>
          <button id="front_year_btn" className="g_btn size_1 l_front_btn s02-nav-btn s02-year-btn" type="button" aria-label="다음 연도" onClick={() => changeMonth(12)}><img src={frontYearIcon} alt="" /></button>
        </div>
        <label className="g_label title center s02-office-label" htmlFor="l_sel_office">지점</label>
        <select id="l_sel_office" className="g_input chosen-select width_2x l_js_e_photo_staff_list s02-office-native" value={route.office} onChange={(event) => replaceLocation({ ...route, office: event.target.value }, appliedFilters)}>{legacyMonthlyOffices.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        <div id="l_sel_office_chosen" className={`chosen-container chosen-container-single s02-office-chosen${officeOpen ? ' chosen-with-drop' : ''}`} ref={officeRef}>
          <button className="chosen-single" type="button" onClick={() => setOfficeOpen((open) => !open)}><span>{selectedOffice.name}</span><b /></button>
          {officeOpen && <div className="chosen-drop"><ul className="chosen-results">{legacyMonthlyOffices.map((item) => <li key={item.id} className={item.id === route.office ? 'result-selected' : 'active-result'}><button type="button" onClick={() => { replaceLocation({ ...route, office: item.id }, appliedFilters); setOfficeOpen(false) }}>{item.name}</button></li>)}</ul></div>}
        </div>
        <LegacyMonthlyFilter open={filterOpen} filters={draftFilters} onOpen={openFilter} onClose={closeFilter} onApply={applyFilters} onToggle={onToggle} />
      </div>
      <div id="l_contract_type" className="g_row gap_10 margin_bottom_10 s02-contract-type">
        <div className="g_input_wrap"><label className="g_label title center">계약촬영</label></div>
        <div className="l_type_chk g_scroll_bar s02-contract-tags"><label className="g_input checkbox_wrap">전체<span className="l_chk_span">{totalContracts}</span></label>{legacyMonthlyContractSummary.map((item) => <label className="g_input checkbox_wrap l_wrap" key={item.id}>{item.name}<span className="l_chk_span">{item.count}</span></label>)}</div>
      </div>
      <div id="l_single_type" className="g_row gap_10 margin_bottom_10 s02-single-type" aria-hidden="true" />
    </section>

    <section className="month_flex_wrap s02-month-flex-wrap">
      <div className="main s02-main">
        <div className="days s02-days">{['일', '월', '화', '수', '목', '금', '토'].map((day) => <div className="day s02-day" key={day}>{day}</div>)}</div>
        <div className="dates s02-dates">
          {cells.map((cell, index) => {
            const entries = cell.monthOffset === 0 ? legacyMonthlyMockEntries.filter((entry) => entry.day === cell.day && entryVisible(entry, appliedFilters)) : []
            const isCurrent = cell.monthOffset === 0
            const holiday = isCurrent ? legacyMonthlyHolidays.find((item) => item.month === route.month && item.day === cell.day) : undefined
            const isToday = isCurrent && route.year === today.getFullYear() && route.month === today.getMonth() + 1 && cell.day === today.getDate()
            return <article id={`day_${index}`} className={`date date_wrap g_scroll_bar s02-date${holiday ? ' is_holiday' : ''}${isToday ? ' today' : ''}`} key={`${cell.monthOffset}-${cell.day}-${index}`} data-year={route.year} data-month={route.month} data-day={cell.day}>
              <div className="date_box s02-date-box">
                {isCurrent && <><div className="month_btn row d-flex justify-content-evenly date_buttons" /><div className="this" title={lunarTitle(route.year, route.month, cell.day)}><span className="calendar_day">{cell.day}</span>일<div className="flex_wrap gap_5 flex_js_center s02-date-actions"><button className="g_btn g_btn_submit size_2" type="button" onClick={() => openDetail(cell.day)}>세부</button><button className="g_btn g_btn_submit size_2" type="button" onClick={() => openMemo(cell.day)}>메모</button></div>{holiday && <div className="l_holiday_name_box">{holiday.name}</div>}</div></>}
              </div>
              {entries.map((entry) => <CalendarEntry entry={entry} onOpen={openEntry} key={entry.id} />)}
            </article>
          })}
        </div>
      </div>
    </section>
  </div>
}
